import React, { Fragment, useContext, memo } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import { formatInTimeZone } from 'date-fns-tz';
import officialscoresheetStyles from '../../officialscoresheet.module.css';
import { ScoreSheetContext } from '../shared/context';
import { useEvalConfig } from '../../hooks';
import {
  formatAttendanceValue,
  getJudgeDisplayContext,
  coachData,
} from '../shared/utils';
import { ScoreSheetLineup, ScoreSheetAA } from '../shared/lineups';
import { ScoreSheetRounds } from '../shared/rounds';
import { convertMillipointsToDisplay } from '../../../../utilities/scoring';
import { editIcon } from '../../../helpers/icons';
import { Clock } from '../shared/clock';

const {
  header,
  row,
  teamScoreSheet,
  reduceFont,
  teamName,
  teamNameLabel,
  teamNameTeam,
  matchDetailsShort,
  matchDetailsLong,
  matchDetailsLabel,
  matchDetailsShortField,
  matchDetailsLongField,
  matchDetailsEditButton,
  body,
  squadLeft,
  squadRight,
  footer,
  subFooter,
  signature,
  signatureLabel,
  signatureField,
  signatureName,
  signatureNames,
  timestamp,
  teamScore,
  teamScoreLabel,
  teamScoreValue,
  teamScoreValueEmpty,
  headJudge,
} = officialscoresheetStyles;

function TeamScoresheetHeader({
  teamA,
  teamB,
  format,
  date,
  timezone,
  location,
  attendance,
  setShowSetup,
}) {
  return (
    <section className={header}>
      <h1>{format.title}</h1>
      <Row className={row}>
        <Col className={teamName}>
          <div className={teamNameLabel}>Home: </div>
          <div className={teamNameTeam}>
            {(teamA && teamA?.forceTitle
              ? `${teamA?.altName}: ${teamA?.displayName}`
              : teamA?.altName) || ''}
            {teamA?.individuals ? ' (Individuals)' : ''}
          </div>
        </Col>
        <Col className={teamName}>
          <div className={teamNameLabel}>Visitor: </div>
          <div className={teamNameTeam}>
            {(teamB && teamB?.forceTitle
              ? `${teamB?.altName}: ${teamB?.displayName}`
              : teamB?.altName) || ''}
            {teamB?.individuals ? ' (Individuals)' : ''}
          </div>
        </Col>
      </Row>
      <Row className={row}>
        <Col className={matchDetailsShort}>
          <div className={matchDetailsLabel}>Date: </div>
          <div className={matchDetailsShortField}>{`${formatInTimeZone(
            new Date(date),
            timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
            "M'/'dd'/'yyyy"
          )}`}</div>
        </Col>
        <Col className={matchDetailsLong}>
          <div className={matchDetailsLabel}>Location: </div>
          <div className={matchDetailsLongField}>{location}</div>
          {location ? null : (
            <Button
              variant="light"
              className={matchDetailsEditButton}
              onClick={() => setShowSetup(true)}
            >
              {editIcon}
            </Button>
          )}
        </Col>
        <Col className={matchDetailsShort}>
          <div className={matchDetailsLabel}>Attendance: </div>
          <div className={matchDetailsShortField}>
            {formatAttendanceValue(attendance)}
          </div>
          {attendance.length !== 0 ? null : (
            <Button
              variant="light"
              className={matchDetailsEditButton}
              onClick={() => setShowSetup(true)}
            >
              {editIcon}
            </Button>
          )}
        </Col>
      </Row>
    </section>
  );
}

function TeamScoresheetBody({
  teamA,
  teamB,
  apparatusArray,
  lineupMax,
  teamsByAthleteId,
}) {
  const lineupMaxes = apparatusArray.map((a, i) => {
    if (teamA?.individuals || teamB?.individuals) {
      return Math.max(
        teamA?.events?.[i]?.lineup?.length || lineupMax,
        teamB?.events?.[i]?.lineup?.length || lineupMax,
        lineupMax
      );
    }

    return lineupMax;
  });

  return (
    <main className={body}>
      <Row className={row}>
        <Col className={squadLeft}>
          {apparatusArray.map((a, i) => (
            <div key={i}>
              <ScoreSheetLineup
                squad={teamA?.events?.[i]?.lineup}
                showSidebar={true}
                apparatus={a}
                max={lineupMaxes?.[i]}
                isIndividuals={teamA?.individuals}
                teamsByAthleteId={teamsByAthleteId}
              />
              <ScoreSheetRounds
                data={!teamA?.individuals ? teamA?.events?.[i] : null}
                apparatus={a}
              />
            </div>
          ))}
        </Col>
        <Col className={squadRight}>
          {apparatusArray.map((a, i) => (
            <div key={i}>
              <ScoreSheetLineup
                squad={teamB?.events?.[i]?.lineup}
                showSidebar={false}
                apparatus={a}
                max={lineupMaxes?.[i]}
                isIndividuals={teamB?.individuals}
                teamsByAthleteId={teamsByAthleteId}
              />
              <ScoreSheetRounds
                data={!teamB?.individuals ? teamB?.events?.[i] : null}
                apparatus={a}
              />
            </div>
          ))}
        </Col>
      </Row>
      <Row>
        <Col className={squadLeft}>
          <Row className={teamScore}>
            <Col className={teamScoreLabel}>
              {!!teamA?.neutral ? 'Team ND: ' : null}
            </Col>
            <Col
              className={
                !!teamA?.neutral ? teamScoreValue : teamScoreValueEmpty
              }
            >
              {!!teamA?.neutral
                ? convertMillipointsToDisplay(teamA.neutral, 1)
                : null}
            </Col>
            {!!teamA && (
              <>
                <Col className={teamScoreLabel}>Final Score: </Col>
                <Col className={teamScoreValue}>
                  {!teamA?.individuals ? teamA?.finalScore : null}
                </Col>
              </>
            )}
          </Row>
        </Col>
        <Col className={squadRight}>
          <Row className={teamScore}>
            <Col className={teamScoreLabel}>
              {!!teamB?.neutral ? 'Team ND: ' : null}
            </Col>
            <Col
              className={
                !!teamB?.neutral ? teamScoreValue : teamScoreValueEmpty
              }
            >
              {!!teamB?.neutral
                ? convertMillipointsToDisplay(teamB.neutral, 1)
                : null}
            </Col>
            {!!teamB && (
              <>
                <Col className={teamScoreLabel}>Final Score: </Col>
                <Col className={teamScoreValue}>
                  {!teamB?.individuals ? teamB?.finalScore : null}
                </Col>
              </>
            )}
          </Row>
        </Col>
      </Row>
      <Row className={row}>
        <Col className={squadLeft}>
          <ScoreSheetAA
            data={teamA?.aa}
            showSidebar={true}
            apparatus={apparatusArray}
            max={lineupMax}
          />
        </Col>
        <Col className={squadRight}>
          <ScoreSheetAA
            data={teamB?.aa}
            showSidebar={false}
            apparatus={apparatusArray}
            max={lineupMax}
          />
        </Col>
      </Row>
    </main>
  );
}

function TeamScoresheetFooter({
  teamA,
  teamB,
  page,
  apparatusArray,
  judgesByApparatus,
  maxJudges,
  session,
  timezone,
  singleSheetOnly,
  sheetOptionsLength,
}) {
  const { hideJudgeNames } = useContext(ScoreSheetContext);
  const evalConfig = useEvalConfig();
  const { dPanel, ePanel, jPanel } = evalConfig;
  const gender = session?.gender;

  const rankIt = (scoreTypes) => {
    const judgeRanking = [...dPanel, ...ePanel, ...jPanel];
    let rank = judgeRanking.length;

    for (let i = 0; i < judgeRanking.length; i++) {
      if (scoreTypes.includes(judgeRanking[i].type)) {
        rank = i;
        break;
      }
    }

    return rank;
  };

  const copyrightTimePageFooter = (page) => {
    return (
      <section className={subFooter}>
        <Row>
          <Col className={timestamp}>
            <span>
              © {new Date().getFullYear()} Virtius&nbsp;&nbsp;»&nbsp;&nbsp;
            </span>
            <Clock timezone={timezone} />
            <span>
              &nbsp;&nbsp;»&nbsp;&nbsp;Page {singleSheetOnly ? 1 : page} of{' '}
              {singleSheetOnly ? 1 : sheetOptionsLength}
            </span>
          </Col>
        </Row>
      </section>
    );
  };

  return (
    <section className={footer}>
      <section className={subFooter}>
        <Row className={row}>
          <Col className={signature} style={{ display: 'block' }}>
            <h5>Signatures:</h5>
            <span className={headJudge}>* Head Judge</span>
          </Col>
        </Row>
        <Row className={row}>
          {apparatusArray.map((a, i) => (
            <Col key={i} className={signature}>
              <div className={signatureLabel}>
                {`${a}: `}
                <sup>*</sup>
              </div>
              <div className={signatureField}></div>
            </Col>
          ))}
        </Row>
        <Row className={row}>
          {apparatusArray.map((a, i) => {
            const headJudge = judgesByApparatus[a].headJudge;

            return (
              <Col key={i} className={signatureNames}>
                {!!headJudge && (
                  <>
                    <div className={signatureLabel}></div>
                    <div className={signatureName}>
                      {!hideJudgeNames
                        ? headJudge.sessionJudgeAssignment?.judge?.name
                        : ''}{' '}
                      {!hideJudgeNames
                        ? getJudgeDisplayContext(headJudge, gender)
                        : ''}
                    </div>
                  </>
                )}
              </Col>
            );
          })}
        </Row>

        {Array(maxJudges || 1)
          .fill('a')
          .map((_, index) => {
            return (
              <Fragment key={index}>
                <Row key={index} className={row}>
                  {apparatusArray.map((a, i) => (
                    <Col key={i} className={signature}>
                      <div className={signatureLabel}>{`${a}: `}</div>
                      <div className={signatureField}></div>
                    </Col>
                  ))}
                </Row>
                <Row className={row}>
                  {apparatusArray.map((a, i) => {
                    const judge = Object.values(judgesByApparatus[a].rest).sort(
                      (a, b) => {
                        if (rankIt(a.scoreTypes) < rankIt(b.scoreTypes)) {
                          return -1;
                        }
                        if (rankIt(a.scoreTypes) > rankIt(b.scoreTypes)) {
                          return 1;
                        }
                        return 0;
                      }
                    )[index];
                    const judgeName =
                      !!judge && judge.sessionJudgeAssignment?.judge?.name;
                    const judgeQual =
                      !!judge && getJudgeDisplayContext(judge, gender);

                    return (
                      <Col key={i} className={signatureNames}>
                        <div className={signatureLabel}></div>
                        <div className={signatureName}>
                          {!hideJudgeNames ? judgeName : ''}{' '}
                          {!hideJudgeNames ? judgeQual : ''}
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </Fragment>
            );
          })}
      </section>
      <section className={subFooter}>
        <Row className={row}>
          <Col className={signature}>
            <div className={signatureLabel}>{`Coach: `}</div>
            <div className={signatureField}></div>
          </Col>
          <Col className={signature}>
            <div className={signatureLabel}>{`Coach: `}</div>
            <div className={signatureField}></div>
          </Col>
        </Row>
        <Row className={row}>
          <Col className={signatureNames}>
            <div className={signatureLabel}></div>
            <div className={signatureName}>
              {!!teamA?.name
                ? `${
                    coachData?.[session.gender]?.[teamA?.rtnId]?.headCoach ??
                    coachData['C1'].name
                  } (${
                    teamA?.forceTitle
                      ? `${teamA?.altName}: ${teamA?.displayName}`
                      : teamA?.altName
                  })`
                : null}
            </div>
          </Col>
          <Col className={signatureNames}>
            <div className={signatureLabel}></div>

            <div className={signatureName}>
              {!!teamB?.name
                ? `${
                    coachData?.[session.gender]?.[teamB?.rtnId]?.headCoach ??
                    coachData['C2'].name
                  } (${
                    teamB?.forceTitle
                      ? `${teamB?.altName}: ${teamB?.displayName}`
                      : teamB?.altName
                  })`
                : null}
            </div>
          </Col>
        </Row>
        <Row className={row}>
          <Col className={signatureNames}>
            <div className={signatureLabel} />
            <div className={signatureName}>
              {teamA?.conductCard ? `Yellow Card: ${teamA?.conductCard}` : null}
            </div>
          </Col>
          <Col className={signatureNames}>
            <div className={signatureLabel} />
            <div className={signatureName}>
              {teamB?.conductCard ? `Yellow Card: ${teamB?.conductCard}` : null}
            </div>
          </Col>
        </Row>
      </section>
      {copyrightTimePageFooter(page)}
    </section>
  );
}

export const TeamScoreSheet = memo(_TeamScoreSheet);

function _TeamScoreSheet({
  teamA,
  teamB,
  page,
  format,
  date,
  timezone,
  location,
  attendance,
  setShowSetup,
  apparatusArray,
  lineupMax,
  teamsByAthleteId,
  judgesByApparatus,
  maxJudges,
  session,
  singleSheetOnly,
  sheetOptionsLength,
}) {
  const evalConfig = useEvalConfig();
  const { ePanel, jPanel } = evalConfig;
  const isReduceFont =
    Math.max(jPanel?.length, ePanel?.length) > 4 ? reduceFont : null;

  return (
    <div className={[teamScoreSheet, isReduceFont].join(' ')}>
      <TeamScoresheetHeader
        teamA={teamA}
        teamB={teamB}
        format={format}
        date={date}
        timezone={timezone}
        location={location}
        attendance={attendance}
        setShowSetup={setShowSetup}
      />
      <TeamScoresheetBody
        teamA={teamA}
        teamB={teamB}
        apparatusArray={apparatusArray}
        lineupMax={lineupMax}
        teamsByAthleteId={teamsByAthleteId}
      />
      <TeamScoresheetFooter
        teamA={teamA}
        teamB={teamB}
        page={page}
        apparatusArray={apparatusArray}
        judgesByApparatus={judgesByApparatus}
        maxJudges={maxJudges}
        session={session}
        timezone={timezone}
        singleSheetOnly={singleSheetOnly}
        sheetOptionsLength={sheetOptionsLength}
      />
    </div>
  );
}
